/**
 * @file Event detail modal owner for the Shared Calendar domain.
 */

import {
    doc,
    getDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import { appData, state } from "../../app/state/state.js";
import {
    closeForm,
    configureDefaultFormActions,
    formModalElement,
} from "../../shared/forms/modalShell.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    escapeHTML,
    formatAndSanitizeUserContent,
} from "../../shared/utils/contentUtils.js";
import { deleteCalendarEvent } from "./calendarApi.js";
import {
    formatCalendarDisplayDate,
    formatCalendarDisplayDateRange,
    formatCalendarDisplayTimeRange,
} from "./calendarDisplayFormatting.js";
import {
    removeCalendarEntry,
} from "./calendarOptimisticEntries.js";
import { openEventFormModal } from "./eventFormModal.js";

function getCurrentUserId() {
    return state.currentUser?.uid || state.currentUserProfile?.id || "";
}

function getUserName(userId) {
    const user = (appData.users || []).find((entry) => entry.id === userId);
    return user?.name || user?.email || userId;
}

function formatUserList(ids = []) {
    const values = Array.isArray(ids) ? ids : [];
    return values.length ?
        values.map(getUserName).join(", ") :
        "None";
}

function formatDateText(eventData) {
    const startDate = eventData?.startDate || "";
    const endDate = eventData?.endDate || startDate;
    const dateLabel = formatCalendarDisplayDateRange(startDate, endDate);
    if (eventData?.isFullDay === true) {
        return `${dateLabel || formatCalendarDisplayDate(startDate)} - All day`;
    }
    const timeLabel = formatCalendarDisplayTimeRange(
        eventData?.startTime,
        eventData?.endTime,
    );
    return [dateLabel, timeLabel].filter(Boolean).join(", ");
}

function canEditEvent(eventData) {
    if (state.currentUserProfile?.role === "admin") {return true;}
    const hostIds = Array.isArray(eventData?.hostIds) ? eventData.hostIds : [];
    return hostIds.includes(getCurrentUserId());
}

function getTodayDateKey() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getCurrentTimeKey() {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
}

function isPastEvent(eventData) {
    const endDate = eventData?.endDate || eventData?.startDate || "";
    if (!endDate) {return false;}

    const today = getTodayDateKey();
    if (endDate < today) {return true;}
    if (endDate > today || eventData?.isFullDay === true) {return false;}

    const endTime = eventData?.endTime || eventData?.startTime || "";
    return Boolean(endTime && endTime <= getCurrentTimeKey());
}

async function fetchEventData(eventId) {
    if (!eventId) {return null;}
    const snapshot = await getDoc(doc(db, "events", eventId));
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

function getDetailRow(label, value) {
    return `
        <div>
            <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                ${escapeHTML(label)}
            </dt>
            <dd class="mt-1 text-sm text-gray-900">${value}</dd>
        </div>
    `;
}

function formatThresholdValue(threshold) {
    const value = Number(threshold?.value);
    const unit = String(threshold?.unit || "");
    if (!Number.isInteger(value) || value <= 0 || !unit) {
        return "";
    }
    const unitLabel = value === 1 ? unit.replace(/s$/, "") : unit;
    return `${value} ${unitLabel} before`;
}

function formatEventThresholds(eventData) {
    const thresholds = Array.isArray(eventData?.notificationThresholds) ?
        eventData.notificationThresholds :
        [];
    const labels = thresholds
        .filter((entry) => entry?.enabled !== false)
        .map(formatThresholdValue)
        .filter(Boolean);
    if (labels.length) {
        return labels.join(", ");
    }
    return "No upcoming reminder";
}

function renderEventDetail(entry, eventData) {
    const data = eventData || entry || {};
    return `
        <div class="space-y-4" data-calendar-event-detail>
            <div>
                <h4 class="text-lg font-semibold text-gray-900">
                    ${escapeHTML(data.title || "Untitled event")}
                </h4>
                <p class="mt-1 text-sm text-gray-500">
                    ${escapeHTML(formatDateText(data))}
                </p>
            </div>
            <dl class="grid grid-cols-1 gap-4 md:grid-cols-2">
                ${getDetailRow("Hosts", escapeHTML(formatUserList(data.hostIds)))}
                ${getDetailRow("Attendees", data.allCalendarUsers === true ?
                    "All users" :
                    escapeHTML(formatUserList(data.attendeeIds)))}
                ${getDetailRow("Reminders", escapeHTML(formatEventThresholds(data)))}
            </dl>
            ${data.description ? `
                <div>
                    <h5 class="text-sm font-semibold text-gray-700">Description</h5>
                    <div class="mt-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                        ${formatAndSanitizeUserContent(data.description)}
                    </div>
                </div>
            ` : ""}
        </div>
    `;
}

function createPreviewActionButton({ label, icon, variant = "brand", onClick }) {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("aria-label", label);
    button.title = label;
    button.dataset.modalCustomActions = "true";
    button.className = [
        "inline-flex h-8 w-8 items-center justify-center rounded-full bg-transparent",
        "text-gray-500 transition-colors focus:outline-none focus-visible:ring-2",
        variant === "danger" ?
            "hover:text-red-600 focus-visible:ring-red-200" :
            "hover:text-[var(--dfc-brand-primary)] focus-visible:ring-[var(--dfc-brand-primary-ring)]",
    ].join(" ");
    button.addEventListener("click", onClick);

    const iconElement = document.createElement("span");
    iconElement.className = "material-symbols-outlined text-[20px] leading-none";
    iconElement.setAttribute("aria-hidden", "true");
    iconElement.textContent = icon;
    button.append(iconElement);
    return button;
}

function configurePreviewActions({ eventData, canEdit }) {
    const cancelBtn = document.getElementById("cancel-form-btn");
    const submitBtn = formModalElement?.querySelector("button[type=\"submit\"]");
    const footer = cancelBtn?.parentElement || submitBtn?.parentElement;
    const formHeaderActions = formModalElement?.querySelector("#form-header-actions");
    if (!cancelBtn || !submitBtn || !footer || !formHeaderActions) {return;}

    formHeaderActions.innerHTML = "";
    footer.style.display = "none";
    submitBtn.style.display = "none";
    cancelBtn.style.display = "none";

    if (canEdit && eventData?.id) {
        const deleteButton = createPreviewActionButton({
            label: "Delete Event",
            icon: "delete",
            variant: "danger",
            onClick: () => openEventDeleteConfirmation(eventData),
        });
        formHeaderActions.append(deleteButton);

        if (!isPastEvent(eventData)) {
            const editButton = createPreviewActionButton({
                label: "Edit Event",
                icon: "edit",
                onClick: () => {
                    closeForm();
                    openEventFormModal(eventData);
                },
            });
            formHeaderActions.append(editButton);
        }
    }

    const closeButton = createPreviewActionButton({
        label: "Close Event Details",
        icon: "close",
        variant: "danger",
        onClick: closeForm,
    });
    formHeaderActions.append(closeButton);
}

function openEventDeleteConfirmation(eventData) {
    const eventName = eventData.title || "this event";
    const getNotifyUsers = () => document
        .querySelector("input[name=\"calendar-event-delete-notify\"]:checked")
        ?.value !== "false";

    openConfirmationModal(
        "Delete Event",
        `
            <div class="space-y-4 text-left">
                <p class="text-sm text-gray-700">
                    Do you want to permanently delete <strong>${escapeHTML(eventName)}</strong>
                    and notify hosts and attendees?
                </p>
                <fieldset class="space-y-2">
                    <legend class="sr-only">Notify affected users</legend>
                    <label class="settings-radio-choice">
                        <input
                            type="radio"
                            name="calendar-event-delete-notify"
                            value="true"
                            class="settings-radio-input sr-only"
                            checked
                        >
                        <span class="settings-radio-marker" aria-hidden="true">
                            <span class="settings-radio-dot"></span>
                        </span>
                        <span>Notify users</span>
                    </label>
                    <label class="settings-radio-choice">
                        <input
                            type="radio"
                            name="calendar-event-delete-notify"
                            value="false"
                            class="settings-radio-input sr-only"
                        >
                        <span class="settings-radio-marker" aria-hidden="true">
                            <span class="settings-radio-dot"></span>
                        </span>
                        <span>Don't notify users</span>
                    </label>
                </fieldset>
            </div>
        `,
        async () => {
            await deleteCalendarEvent(
                eventData.id,
                eventData.updatedAt,
                getNotifyUsers(),
            );
            removeCalendarEntry("event", eventData.id);
            closeForm();
        },
        true,
        "Delete",
        null,
        "Deleting...",
        "Cancel",
        {
            actionsLayout: "right",
        },
    );
}

/**
 * Opens read-only Calendar event detail.
 * @param {object} entry Display-safe calendar entry.
 * @returns {void}
 */
export function openEventDetailModal(entry) {
    if (!formModalElement) {
        console.error("Form modal element (#form-modal) not found.");
        return;
    }

    configureDefaultFormActions();
    const formTitle = formModalElement.querySelector("#form-title");
    const formHeaderActions = formModalElement.querySelector("#form-header-actions");
    const formFields = formModalElement.querySelector("#form-fields");
    const itemForm = formModalElement.querySelector("#item-form");
    if (!formTitle || !formHeaderActions || !formFields || !itemForm) {
        console.error("Required elements missing within #form-modal.");
        return;
    }

    formTitle.textContent = "Event Details";
    formHeaderActions.innerHTML = "";
    itemForm.onsubmit = (event) => event.preventDefault();
    formFields.innerHTML = renderEventDetail(entry, entry);
    configurePreviewActions({ eventData: null, canEdit: false });
    formModalElement.classList.add("flex");

    fetchEventData(entry?.sourceId)
        .then((eventData) => {
            if (!eventData) {return;}
            formFields.innerHTML = renderEventDetail(entry, eventData);
            configurePreviewActions({
                eventData,
                canEdit: canEditEvent(eventData),
            });
        })
        .catch((error) => {
            console.error("Error loading Calendar event detail:", error);
        });
}
